import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { resetPassword as resetPasswordAction, sendOtp as sendOtpAction } from 'modules/state-mgmt/user';
import { prettifyErrorResponse } from 'utils';

interface IUseResetPassword {
  onSuccess?: () => void;
  onFailure?: (backendErrors: any) => void;
}

interface IUseSendOtp {
  onSuccess?: (email?: string, mobilePhoneNumber?: string) => void;
  onFailure?: (backendErrors: any) => void;
}

export const useResetPassword = ({ onSuccess, onFailure }: IUseResetPassword) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const resetPassword = async (email?: string, mobilePhoneNumber?: string) => {
    setIsLoading(true);

    try {
      const payload: any = {};
      if (email) payload.email = email;
      if (mobilePhoneNumber) payload.mobilePhoneNumber = mobilePhoneNumber;

      await dispatch(resetPasswordAction(payload)).unwrap();

      setIsLoading(false);
      onSuccess?.();
    } catch (error) {
      setIsLoading(false);
      onFailure?.(error);
      enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
    }
  };

  return { isLoading, resetPassword };
};

/**
 * Sends a one-time password instead of a reset link. Used when the portal runs inside an
 * admin session, where the worker cannot open an emailed reset link on the admin's device (CH-4506).
 */
export const useSendOtp = ({ onSuccess, onFailure }: IUseSendOtp) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { isOnline, languageCode } = useAppSelector(state => ({
    isOnline: state.app?.isOnline,
    languageCode: state.app?.languageCode,
  }));

  const sendOtp = async (email?: string, mobilePhoneNumber?: string) => {
    // Sending and verifying an OTP both need the network — there is nothing sensible to queue.
    if (!isOnline) {
      enqueueSnackbar(t('common.offlineError'), { variant: 'error' });
      return;
    }

    setIsLoading(true);

    try {
      await dispatch(sendOtpAction({ email, mobilePhoneNumber, languageCode })).unwrap();

      setIsLoading(false);
      onSuccess?.(email, mobilePhoneNumber);
    } catch (error) {
      setIsLoading(false);
      onFailure?.(error);
      enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
    }
  };

  return { isLoading, sendOtp };
};
